import { showError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createApi } from '@reduxjs/toolkit/query/react'
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query'

import { getUserServiceToken } from 'root-api'

const getDefaultGraphqlUrl = () => {
  const value = typeof window !== 'undefined' && window.ROOT_API_GRAPHQL_BASE
  return (value && String(value)) || '/api/search'
}


//  True when GraphQL is served from this machine (e.g. supergraph on :4000).
const isLocalGraphqlUrl = (graphqlUrl) => {
  if (!graphqlUrl) return false
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const { hostname } = new URL(String(graphqlUrl), origin)
    return hostname === 'localhost' || hostname === '127.0.0.1'
  } catch {
    return false
  }
}

const dynamicGraphqlBaseQuery = async (args, api, extraOptions) => {
  const handleErrors = extraOptions?.handleErrors !== false
  const tokenElement = document.querySelector('meta[name=csrf-token]')
  const token = tokenElement ? tokenElement.content : null

  const url = args?.meta?.baseUrl || getDefaultGraphqlUrl()
  const requestHeaders = {
    'X-CSRF-Token': token,
    ...(args?.meta?.headers ?? {}),
  }

  // Some GraphQL backends (e.g. CRM in local dev) expect a Bearer JWT. Mint one via
  // `POST /api/restricted/user_service_tokens` only in development when the GraphQL URL
  // is local — staging/production use the normal publisher session (cookies + CSRF),
  // same as `/api/search` meetings GraphQL.
  // That endpoint returns 403 for browser callers on many staging/prod setups.
  // Service tokens are short-lived; forceRefetch avoids a stale cached JWT during local polling.
  // The isLocalGraphqlUrl check prevents development builds from requesting service tokens for remote GraphQL
  // endpoints, such as Marina's staging configuration.
  const shouldFetchAuthToken = process.env.NODE_ENV === 'development'
    && args?.meta?.fetchAuthToken
    && isLocalGraphqlUrl(url)
  if (shouldFetchAuthToken) {
    const { data: authToken } = await api.dispatch(
      getUserServiceToken.initiate(undefined, { forceRefetch: true }),
    )

    if (authToken) {
      requestHeaders.Authorization = `Bearer ${authToken}`
    }
  }

  const rawBaseQuery = graphqlRequestBaseQuery({
    url,
    requestHeaders,
  })

  const treatGraphqlErrorsAsFailure = args?.meta?.treatGraphqlErrorsAsFailure === true

  const asResult = (data, graphqlErrors) => {
    const errors = graphqlErrors || []
    if (treatGraphqlErrorsAsFailure && errors.length > 0) {
      return {
        error: {
          status: 'GRAPHQL_ERROR',
          data: { _graphqlErrors: errors },
          error: errors[0]?.message || 'GraphQL error',
        },
      }
    }
    return { data: { ...data, _graphqlErrors: errors } }
  }

  try {
    const response = await rawBaseQuery(args, api, extraOptions)

    // Handle GraphQL partial success: extract data even when errors exist
    // GraphQL can return both errors and data in the same response
    // graphqlRequestBaseQuery may return { error: ... } even when data exists

    // First, check if the response itself is a GraphQL response with both data and errors
    // This can happen when graphqlRequestBaseQuery returns the raw GraphQL response
    if (response.data && response.errors && !response.error) {
      // Response is already in the correct format: { data: {...}, errors: [...] }
      return asResult(response.data, response.errors)
    }

    // Check if the GraphQL response is in meta.response (common structure from graphqlRequestBaseQuery)
    if (response.meta?.response) {
      const metaResponse = response.meta.response
      if (metaResponse.data || metaResponse.errors) {
        return asResult(metaResponse.data || {}, metaResponse.errors)
      }
    }

    if (response.error) {
      // Check multiple locations for the raw GraphQL response with data
      // The error object might contain the raw response in different places
      let graphqlResponse = null
      let graphqlErrors = null

      // Check if error has the raw GraphQL response
      if (response.error.originalStatus) {
        graphqlResponse = response.error.originalStatus
        graphqlErrors = graphqlResponse.errors
      } else if (response.error.data) {
        // Check if error.data is the GraphQL response itself
        if (response.error.data.data || response.error.data.errors) {
          graphqlResponse = response.error.data
          graphqlErrors = response.error.data.errors
        } else {
          // error.data might be the data field directly
          graphqlResponse = { data: response.error.data }
          graphqlErrors = response.error.errors || []
        }
      } else if (response.error.errors || response.error.data) {
        // Error object itself might be the GraphQL response
        graphqlResponse = response.error
        graphqlErrors = response.error.errors
      }

      // Also check if error.response contains the GraphQL response
      if (!graphqlResponse?.data && response.error.response) {
        const errorResponse = response.error.response
        if (errorResponse.data || errorResponse.errors) {
          graphqlResponse = errorResponse
          graphqlErrors = errorResponse.errors
        } else if (errorResponse.data?.data || errorResponse.data?.errors) {
          graphqlResponse = errorResponse.data
          graphqlErrors = errorResponse.data.errors
        }
      }

      // Also check if the error has a nested structure where the GraphQL response is in error.error
      // Some GraphQL libraries wrap the response in multiple layers
      if (!graphqlResponse?.data && response.error.error) {
        const nestedError = response.error.error
        if (nestedError.data || nestedError.errors) {
          graphqlResponse = nestedError
          graphqlErrors = nestedError.errors
        }
      }

      // If we found GraphQL data, return it as success with errors attached
      if (graphqlResponse?.data) {
        return asResult(graphqlResponse.data, graphqlErrors)
      }

      // No data found, show error unless caller opted out (e.g. 404 fallback)
      if (handleErrors) {
        api.dispatch(showError(I18n.t('errors.unknown')))
      }
    }

    // If response has data, check if it also has errors attached
    // Some GraphQL libraries return data even with errors
    if (response.data) {
      // Check if the data object itself contains errors (from the raw GraphQL response)
      if (response.data.errors) {
        return asResult(response.data, response.data.errors)
      }
      // Also check if there's a nested structure with errors
      // Some GraphQL responses wrap data in a structure like { data: { meetings: {...} }, errors: [...] }
      if (response.errors) {
        return asResult(response.data, response.errors)
      }
      // Check if response.data is the GraphQL response itself with nested data and errors
      // graphqlRequestBaseQuery might return { data: { data: {...}, errors: [...] } }
      if (response.data.data && response.data.errors) {
        return asResult(response.data.data, response.data.errors)
      }
    }

    return response
  } catch (error) {
    if (handleErrors) {
      api.dispatch(showError(I18n.t('errors.server.server')))
    }
    throw error
  }
}

export const rootApi = createApi({
  reducerPath: 'graphqlApi',
  baseQuery: dynamicGraphqlBaseQuery,
  endpoints: () => ({}),
})
